import React, { useContext, useEffect, useRef } from 'react';
import { generateId } from '../../utility/functions';
import { QuestRoute } from '../../api/QuestRoutes';
import Button from '../../forms/Button';
import ModalFactory from '../../Modal';
import HamQuest from '../../api/HamQuestGiver';
import { UserContexts } from '../../stateProvider/UsersContext';
import AddNewCustomer from '../../processors/AddNewCustomer';

// duplicated from LicensePlateReads, importing it from there would create an import cycle
const emptyGuid = '00000000-0000-0000-0000-000000000000';
const hasCustomer = (e) => !!e.customerId && e.customerId !== emptyGuid;

// Row actions for a plate read (add backup plate, block club, mark resolved, etc.),
// shared by every page that lists plate reads across customers.
// refresh is called after an action changes data so the host page can reload.
const usePlateReadActions = (refresh) => {
    const userContext = useContext(UserContexts);
    const userContextRef = useRef(userContext);

    useEffect(() => {
        userContextRef.current = userContext;
    }, [userContext]);

    const refreshRef = useRef(refresh);
    refreshRef.current = refresh;
    const refreshData = () => refreshRef.current?.();

    const renderActionButtons = (e) => {
        var r = [];

        var blockable = ['UnregisteredPlate'];

        if(e.flags.includes('UnregisteredPlate'))
        {
            //add backup plate
            r.push(<Button key={generateId()} icon='plus' onClick={() => awaitUserContextAndExecute(e.customerId, () => addBackupPlateForCustomer(e.customerId, e.plate, e.transRecordId))} label='Add Backup Plate'/>);

            //add family member
            r.push(<Button key={generateId()} icon='family' onClick={() => awaitUserContextAndExecute(e.customerId, () => createCustomerAccount(e.customerId, e.plate, e.transRecordId, true ))} label='Add Child Account'/>);
        }

        //block account
        if(e.flags.some(f => blockable.includes(f)))
            r.push(<Button key={generateId()} icon='lock' onClick={() => awaitUserContextAndExecute(e.customerId, () => showBlockOptionsModal(e.customerId, e.customerName, e.plateReadId, e.transRecordId))} label='Block Club'/>);

        //mark resolved / clear flags
        if(e.flags?.length > 0)
            r.push(<Button key={generateId()} icon='checkCircle' onClick={() => markResolved(e.transRecordId)} label='Mark Resolved' />)

        //visitor - create account
        if(!hasCustomer(e))
            r.push(<Button key={generateId()} icon='userPlus' onClick={() => createCustomerAccount(e.customerId, e.plate, e.transRecordId, false)} label='Make Customer Account'/>);

        return r;
    }

    const awaitUserContextAndExecute = async (customerId, callback) => {
        //refresh context each time even if same customer Id
        const modalId = generateId();
        if(!customerId || customerId === '')
            ModalFactory.show({
                title: 'Error',
                type: 'error',
                body: <>Error loading user details.</>
            });
        else
            await ModalFactory.promise({
                id: modalId,
                title: 'Loading customer details',
                callback: () => userContext.manualRefresh(customerId)
            }).then(() => {
                ModalFactory.close(modalId);
                callback();
            });
    }

    const addBackupPlateForCustomer = (customerId, plate, transRecordId) => {
        const uc = userContextRef.current;
        const payload = {
                status: 'Active',
                mediaType: 'LicensePlate',
                backupPlate: true,
                mediaId: plate
        }

        if (!uc.user || (uc.user.userId ?? '').toLowerCase() !== (customerId ?? '').toLowerCase())
            ModalFactory.show({
                title: 'Add Plate',
                type: 'error',
                body: <>Error loading user details.</>
            });
        else
            ModalFactory.confirmAndProcess({
                confirmation: {
                    title: uc.user.fullName + ' - Add Plate',
                    body: <span>Add plate <b>{plate}</b> as backup media for {uc.user.fullName}?</span>
                },
                promise: {
                    title: 'Adding',
                    body: `Adding, please wait.`,
                    callback: () => HamQuest.post(QuestRoute.user.mediaIDs(uc.user.userId), payload).then(() => markResolvedCallback(transRecordId))
                },
                confirmed: {
                    title: 'Success',
                    body: `${plate} added as a backup License Plate.`,
                    type: 'success'
                },
                finally: () => {
                    //TODO: need to clear cached user context if same as user just acted upon
                    userContext.manualRefresh(customerId);
                    refreshData();
                }
            })
    }

    const createCustomerAccount = async (customerId, plate, transRecordId, createAsChild) => {
        const uc = userContextRef.current;

        const childProps = createAsChild ? {isChild: true, parentId: customerId} : {};
        const modalId = generateId();

        const callBack = createAsChild ? () => markResolvedCallback(transRecordId) : () => updatePlateReadsCustomerIdCallback();

        if(createAsChild && (!uc.user || (uc.user.userId ?? '').toLowerCase() !== (customerId ?? '').toLowerCase()))
            ModalFactory.show({
                id: modalId,
                title: 'Add Child Account',
                type: 'error',
                body: <>Error loading parent user details.</>
            });
        else if (createAsChild && uc.washClub.status !== 'Active')
            ModalFactory.show({
                id: modalId,
                title: uc.user.fullName + ` - Add Child Account`,
                type: 'error',
                body:   <>
                            <p>The user's wash club account is in <b>{uc.washClub.status}</b> status.</p>
                            <div>No additional action can be taken at this time.</div>
                        </>
            });
        else
            ModalFactory.show({
                id: modalId,
                title: createAsChild ? uc.user.fullName + ` - Add Child Account` : 'New Customer',
                type: 'info',
                body: <AddNewCustomer modalId={modalId} license={plate} autoAddToPlan {...childProps} callback={callBack} />
            });
    }

    const showBlockOptionsModal = (customerId, customerName, plateReadId) => {
        let uc = userContextRef.current;
        const id = generateId();

        let title = '';
        let body = <></>;
        let buttons = [];
        let modalType = 'info';

        switch(uc?.washClub?.status ?? 'nodata')
        {
            case 'Suspended':
                title = uc.user.fullName + ' - Block Club Access';
                modalType = 'warning';
                body =  (<>
                            <p>The wash club account is already in <b>Suspended</b> status.</p>
                            <div>If desired, you can add this transaction to the list of violations</div>
                            <div>or kick the user from the club and immediately deactivate</div>
                            <div>the wash club account.</div>
                        </>);
                buttons.push(<Button label='Add Violation' onClick={() => {ModalFactory.close(id); setClubSharedStatusForCustomer(customerId, customerName, plateReadId);}}/>);
                buttons.push(<Button label='Kick From Club' onClick={() => {ModalFactory.close(id); setKickedFromClubStatusForCustomer(customerId, customerName, plateReadId);}}/>);
                buttons.push(<Button label='Cancel' onClick={() => {ModalFactory.close(id)}}/>);
                break;
            case 'Inactive':
                title = uc.user.fullName + ' - Block Club Access';
                modalType = 'error';
                body =  (<>
                            <p>The user's wash club account is in <b>Inactive</b> status.</p>
                            <div>No additional action can be taken at this time.</div>
                        </>);
                        buttons.push(<Button label='close' onClick={() => {ModalFactory.close(id)}}/>);
                break;
            case 'nodata':
                title = 'Error - Block Club Access';
                modalType = 'error';
                body =  (<>
                            <div>Error loading customer details.</div>
                        </>);
                        buttons.push(<Button label='close' onClick={() => {ModalFactory.close(id)}}/>);
                break;
            default:
                title = uc.user.fullName + ' - Block Club Access';
                modalType = 'info';
                body =  (<>
                            <p>Block club access for customer?</p>
                            <div>Suspend access pending customer payment for the vended wash</div>
                            <div>or kick the customer from the club and immediately deactivate</div>
                            <div>the user wash club account.</div>
                        </>);
                buttons.push(<Button label='Suspend Club' onClick={() => {ModalFactory.close(id); setClubSharedStatusForCustomer(customerId, customerName, plateReadId);}}/>);
                buttons.push(<Button label='Kick From Club' onClick={() => {ModalFactory.close(id); setKickedFromClubStatusForCustomer(customerId, customerName, plateReadId);}}/>);
                buttons.push(<Button label='Cancel' onClick={() => {ModalFactory.close(id)}}/>);
                break;
        }

        ModalFactory.show({
            id,
            type: modalType,
            title: title,
            body:   body,
            hideClose: true,
            buttons: buttons
        });
    }

    const setClubSharedStatusForCustomer = (customerId, customerName, plateReadId) => {
        const modalId = generateId();
        ModalFactory.promise({
            id: modalId,
            title: 'Updating',
            body: `Updating, please wait.`,
        });

        HamQuest.post(QuestRoute.user.washClubSetClubShared(customerId, plateReadId))
        .then(() => {
            ModalFactory.update({
                id: modalId,
                type: 'success',
                title: 'Success',
                body: <>Wash club set to SUSPENDED status for {customerName}.</>
            })
            .then(() => {
                userContext.manualRefresh(customerId);
                refreshData();
            });
        })
        .catch(ex => {
            ModalFactory.close(modalId);
            ModalFactory.catch(ex);
            refreshData();
        });
    };

    const setKickedFromClubStatusForCustomer = (customerId, customerName, plateReadId) => {
        const id = generateId();
        ModalFactory.promise({
            id,
            title: 'Updating',
            body: `Updating, please wait.`,
        });

        HamQuest.post(QuestRoute.user.washClubKickFromClub(customerId, plateReadId))
        .then(() => {
            ModalFactory.update({
                id,
                type: 'success',
                title: 'Success',
                body: `Club status set to Inactive.`
            })
            .then(() => {
                userContext.manualRefresh(customerId);
                refreshData();
            });
        })
        .catch(ex => {
            ModalFactory.close(id);
            ModalFactory.catch(ex);
            refreshData();
        });
    }

    const markResolved = (transRecordId) => {
        ModalFactory.confirmAndProcess({
            confirmation: {
                title: 'Mark Resolved',
                type: 'warning',
                body: <>
                        <p>This will resolve and clear all alerts for this transaction.</p>
                        <p>Are you sure?</p>
                      </>,
            },
            promise: {
                title: 'Updating',
                body: `Updating, please wait.`,
                callback: () => markResolvedCallback(transRecordId)
            },
            confirmed: {
                type: 'success',
                title: 'Success',
                body: `All flags marked as resolved for transaction.`
            }
        });
    };

    const markResolvedCallback = (transRecordId) => {
        const payload = [
                transRecordId
        ];
        return new Promise(async (resolve, reject) => {
                await HamQuest.post(QuestRoute.plateReads.markResolved, payload)
                .then(resolve(true))
                .catch(reject({ error: { errorMessage: 'Error markings alert flags as resolved.' } }))
                .finally(() => refreshData())
        })
    }

    const updatePlateReadsCustomerIdCallback = () => {
        const customerId = userContextRef.current.user.customerId;
        return new Promise(async (resolve, reject) => {
                await HamQuest.post(QuestRoute.plateReads.updateCustomer, {customerId})
                .then(resolve(true))
                .catch(reject({ error: { errorMessage: 'Error updating PlateRead records for new customer.' } }))
                .finally(() => refreshData())
        })
    }

    return { renderActionButtons };
}

export default usePlateReadActions;
